import React, { useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../config/firebaseConfig";

/* 🔹 Helper: convert Roman numerals or plain numbers */
const romanToInt = (s = "") => {
  if (!s) return 0;

  // If already numeric, return directly
  if (!isNaN(s)) return parseInt(s, 10);

  const map = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  s = s.toUpperCase().replace(/[^IVXLCDM]/g, "");
  let total = 0;
  for (let i = 0; i < s.length; i++) {
    const cur = map[s[i]] || 0;
    const next = map[s[i + 1]] || 0;
    if (cur < next) total -= cur;
    else total += cur;
  }
  return total;
};

/* 🔹 Normalize raw className into “Class X” form */
const normalizeClass = (raw) => {
  if (!raw && raw !== 0) return null;
  const v = String(raw).trim();
  const low = v.toLowerCase();

  // handle nursery/lkg/ukg
  if (low.includes("nursery")) return "Nursery";
  if (low.includes("lkg")) return "LKG";
  if (low.includes("ukg")) return "UKG";

  // remove “class”, “std”, “th”, etc.
  let cleaned = v.replace(/class|std|grade|gr|th|st|nd|rd/gi, "").trim();

  // Roman numeral
  const romanMatch = cleaned.match(/^[ivxlcdmIVXLCDM]+$/);
  if (romanMatch) {
    const num = romanToInt(romanMatch[0]);
    if (num >= 1 && num <= 12) return `Class ${num}`;
  }

  // Numeric value
  const numMatch = cleaned.match(/^\d+$/);
  if (numMatch) {
    const num = parseInt(numMatch[0], 10);
    if (num >= 1 && num <= 12) return `Class ${num}`;
  }

  return v; // fallback
};

/* 🔹 Main Component */
export default function ClassUpdaterButtons() {
  const [fetching, setFetching] = useState(false);
  const [applying, setApplying] = useState(false);
  const [changes, setChanges] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  /* 🔸 Fetch all students whose className is only numeric (like “1”, “2”, “3”) */
  const handleFetch = async () => {
    setFetching(true);
    setError(null);
    setMessage("");
    setChanges([]);

    try {
      const snap = await getDocs(collection(db, "students"));
      const next = [];

      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const current = (data?.className || "").trim();

        // only pick numeric or roman class names
        if (/^\d+$/.test(current) || /^[ivxlcdmIVXLCDM]+$/.test(current)) {
          const normalized = normalizeClass(current);
          if (normalized && normalized !== current) {
            next.push({ id: docSnap.id, current, normalized });
          }
        }
      });

      setChanges(next);
      setMessage(`${next.length} students found needing update.`);
    } catch (err) {
      setError(err.message || "Fetch failed");
    } finally {
      setFetching(false);
    }
  };

  /* 🔸 Confirm and apply updates */
  const handleApply = async () => {
    if (changes.length === 0) {
      setMessage("No changes to apply.");
      return;
    }
    setShowConfirm(true);
  };

  const confirmApply = async () => {
    setShowConfirm(false);
    setApplying(true);
    setError(null);

    let applied = 0;
    try {
      for (const ch of changes) {
        try {
          const ref = doc(db, "students", ch.id);
          await updateDoc(ref, {
            className: ch.normalized,
            updatedAt: Timestamp.now(),
          });
          applied++;
        } catch (updateErr) {
          console.error("Update failed for", ch.id, updateErr);
        }
      }
      setMessage(
        `Applied ${applied} / ${changes.length} updates successfully.`
      );
      setChanges([]);
    } catch (err) {
      setError(err.message || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="max-w-xl bg-white p-4 rounded shadow-sm relative">
      <div className="flex flex-wrap gap-3">
        {/* 🔵 Fetch button */}
        <button
          onClick={handleFetch}
          disabled={fetching || applying}
          className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
        >
          {fetching ? "Fetching..." : "Fetch Numeric Students"}
        </button>

        {/* 🟢 Apply button */}
        <button
          onClick={handleApply}
          disabled={changes.length === 0 || fetching || applying}
          className={`px-3 py-1 rounded ${
            changes.length === 0
              ? "bg-gray-300 text-gray-700"
              : "bg-green-600 text-white hover:bg-green-700"
          }`}
        >
          {applying ? "Applying..." : `Apply updates (${changes.length})`}
        </button>
      </div>

      {message && <div className="mt-3 text-sm text-gray-700">{message}</div>}
      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}

      {/* 🔍 Show pending changes */}
      {changes.length > 0 && (
        <div className="mt-3 text-sm">
          <div className="font-medium mb-2">Students to Update:</div>
          <div className="max-h-40 overflow-auto border rounded p-2 bg-gray-50">
            {changes.map((c) => (
              <div key={c.id} className="mb-2">
                <div className="text-xs text-gray-500 break-all">{c.id}</div>
                <div>
                  <span className="text-red-600 mr-2">
                    {c.current || "(empty)"}
                  </span>
                  <span className="text-green-700">{c.normalized}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 🧾 Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded p-5 shadow-lg max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-2 text-gray-800">
              Confirm Update
            </h2>
            <p className="text-sm text-gray-700 mb-4">
              Are you sure you want to update <strong>{changes.length}</strong>{" "}
              student records to normalized format (e.g., “Class 1”, “Class 2”,
              etc.)?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={confirmApply}
                className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
              >
                Yes, Apply
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="bg-gray-300 text-gray-800 px-3 py-1 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
